/**
 * EditAppointmentDialog
 *
 * Edit content fields on an existing appointment WITHOUT touching the
 * schedule (use Reschedule) or status (use the Mark-* buttons).
 *
 * Editable: carrier, client, PO/consignees, document type/number,
 * reference, comments, required capabilities, and direction.
 *
 * Locked: AppointmentId, AppointmentNumber, ScheduledStart/End, DockDoor,
 * Status, ActualArrival/Departure. Direction is server-restricted to
 * REQUESTED/APPROVED; we mirror that constraint here for UX (greyed-out
 * with a tooltip explanation).
 */
import { useEffect, useState } from 'react';
import { parseISO } from 'date-fns';
import { AlertCircle, Loader2 } from 'lucide-react';
import { printDocumentToDialog } from '@/lib/printDocument';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { PoFinder } from '@/components/PoFinder';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    ApiCallError,
    editAppointment,
    setActualTimes,
    EditAppointmentBody,
    adminListCarrierDrivers,
    addAppointmentDriver,
    removeAppointmentDriver,
    CarrierDriverAdminRow,
    getCheckIns,
} from '@/api/client';
import type { AppointmentDto, CalendarDoorDto, CheckInDto, DocumentDto, LookupsDto } from '@/api/types';
import { NO_SELECTION } from '@/lib/utils';
import { SignDocumentDialog } from '@/components/SignDocumentDialog';
import { PaperworkList } from '@/components/PaperworkList';
import { DepartureChecklistPanel } from '@/components/DepartureChecklistPanel';

interface EditAppointmentDialogProps {
    open: boolean;
    onClose: () => void;
    appt: AppointmentDto;
    doors: CalendarDoorDto[];
    lookups: LookupsDto;
    /** `schedule` is present only when door/date/time actually changed — the
     *  caller reschedules (with the usual cap/dedication + notify prompts). */
    onSaved: (
        updated: AppointmentDto,
        schedule?: { dockDoorId: number | null; newStartUtc: string; newEndUtc: string },
    ) => void;
    getToken: () => Promise<string>;
}

export function EditAppointmentDialog({
    open,
    onClose,
    appt,
    doors,
    lookups,
    onSaved,
    getToken,
}: EditAppointmentDialogProps) {
    // Direction is editable only in REQUESTED/APPROVED — server enforces this
    // but we mirror the constraint in the UI so the user understands.
    const directionEditable =
        appt.statusCode === 'REQUESTED' || appt.statusCode === 'APPROVED';

    /**
     * ⭐ SIGNING FROM THE EDIT SCREEN. A second mount of SignDocumentDialog, safe
     * for the same reason as the one on the calendar page: it takes a document
     * and hands back a signed copy, holding no state of its own that could drift.
     */
    const [signRow, setSignRow] = useState<DocumentDto | null>(null);
    /** ⭐ Bumped after a signing so the list rebuilds — the signed copy is a SECOND row. */
    const [signedTick, setSignedTick] = useState(0);

    /**
     * ---- ⭐ PRINT, NOT DOWNLOAD (Pete, 2026-09-10) -------------------------
     *
     * ⛔ THIS USED TO CALL `getDocumentUrl` AND `window.open`, which downloads:
     * every SAS `blobClient` mints is stamped `Content-Disposition: attachment`
     * on purpose, so opening one is a download by definition. It was the button
     * asking the wrong endpoint, not a bug in the button.
     *
     * ⭐ `printDocumentToDialog` goes through the inline preview route instead
     * and opens the OS print dialog — every printer the workstation has, laser
     * included. See `lib/printDocument.ts` for why Acrobat cannot be launched.
     */
    const [printMsg, setPrintMsg] = useState<string | null>(null);
    const openForPrint = async (d: DocumentDto) => {
        setPrintMsg(null);
        const outcome = await printDocumentToDialog(
            getToken, { kind: 'document', documentId: d.documentId }, d.fileName);
        /* ⛔ SHOWN EVEN ON SUCCESS. "The print dialog is open" is the only
           feedback that distinguishes a working button from a dead one when the
           dialog opens behind the window. */
        setPrintMsg(outcome.message);
    };

    const [direction, setDirection] = useState<'INBOUND' | 'OUTBOUND' | 'BLOCK'>(appt.direction);
    const [dockDoorId, setDockDoorId] = useState<number | null>(appt.dockDoorId);
    const [startLocal, setStartLocal] = useState<string>(toDatetimeLocalString(parseISO(appt.scheduledStartUtc)));
    const [endLocal, setEndLocal] = useState<string>(toDatetimeLocalString(parseISO(appt.scheduledEndUtc)));
    const [carrierCode, setCarrierCode] = useState<string>(appt.carrierCode);
    const [clientCode, setClientCode] = useState<string>(appt.clientCode);
    const [documentType, setDocumentType] = useState<string>(appt.documentType || 'PO');
    const [poNumber, setPoNumber] = useState<string>(appt.poNumber ?? '');
    const [referenceNumber, setReferenceNumber] = useState<string>(appt.referenceNumber ?? '');
    // ⭐ #130 — and THE EDIT SCREEN IS WHERE THIS MATTERS MOST. A mass-created
    // appointment (#66) arrives with nothing in it; the scheduler finds the numbers
    // in the WMS and types them here. The container number had no field at all
    // until now, despite the column existing since migration 139.
    const [containerNumber, setContainerNumber] = useState<string>(appt.containerNumber ?? '');
    const [palletCount, setPalletCount] = useState<string>(appt.palletCount != null ? String(appt.palletCount) : '');
    const [comments, setComments] = useState<string>(appt.comments ?? '');
    /**
     * ⭐ THE TIMES THE TRUCK REALLY KEPT (#138).
     *
     * ⚠️ SEEDED FROM THE APPOINTMENT AND HELD AS THE datetime-local STRING, matching
     * how this dialog already handles the scheduled window — the browser's local time
     * IS the facility's, which is the convention the rest of this file assumes.
     * ⛔ Inventing a second timezone convention in one dialog would guarantee the two
     * disagree the first time somebody edits both.
     */
    const [actualArrivalLocal, setActualArrivalLocal] = useState<string>(
        appt.actualArrivalUtc ? toDatetimeLocalString(new Date(appt.actualArrivalUtc)) : '');
    const [actualDepartureLocal, setActualDepartureLocal] = useState<string>(
        appt.actualDepartureUtc ? toDatetimeLocalString(new Date(appt.actualDepartureUtc)) : '');
    const [capabilityIds, setCapabilityIds] = useState<number[]>([]);
    const [consignees, setConsignees] = useState<
        { consigneeCode: string; shipmentNumber: string }[]
    >(
        appt.consignees.length > 0
            ? appt.consignees.map((c) => ({
                  consigneeCode: c.consigneeCode,
                  shipmentNumber: c.shipmentNumber ?? '',
              }))
            : [{ consigneeCode: '', shipmentNumber: '' }],
    );

    // Drivers — an appointment can have several (multi-truck pickup, or an LTL
    // appointment booked with a longer window; see migration 134). This dialog
    // is a Save-based form, so driver changes are STAGED locally and committed
    // on Save alongside everything else (not fired immediately like the
    // standalone AssignDriverDialog). `stagedDrivers` is the working list:
    //   - entries with appointmentDriverId came from the appointment (existing)
    //   - entries without it are pending adds (roster pick or brand-new driver)
    // On Save we diff this against appt.drivers to compute removes + adds.
    // Scoped to whichever carrier is selected above; changing Carrier resets it.
    type StagedDriver = {
        appointmentDriverId: number | null;   // null = pending add
        driverId: number | null;              // set for roster picks & existing; null for brand-new
        name: string;
        phone: string;
        email?: string | null;                // only carried for brand-new drivers (roster create)
    };
    const [stagedDrivers, setStagedDrivers] = useState<StagedDriver[]>(
        appt.drivers.map((d) => ({ appointmentDriverId: d.appointmentDriverId, driverId: d.driverId, name: d.name, phone: d.phone })),
    );
    const [driverMode, setDriverMode] = useState<'existing' | 'new'>('existing');
    /**
     * ---- ⭐⭐ THE PICK IS A KEYED STRING, NOT A DRIVER ID (Pete, 2026-09-10) --
     *
     * > *"The Select A driver should list any drivers in the Driver Check-Ins no
     * > matter what carrier they may have been assigned too."*
     *
     * ⛔ THE OLD LIST WAS EMPTY WHENEVER THE CARRIER FIELD WAS. The roster only
     * loads `if (!open || !carrierCode) return`, so an appointment with no
     * carrier — which is most of them at Brighton — offered nothing at all.
     *
     * ⚠️ AND A CHECKED-IN DRIVER MAY NOT BE ON ANY ROSTER, so a numeric driver
     * id cannot address him. The key is `r:<driverId>` for a roster pick and
     * `c:<checkInId>` for an arrival — two different things that must not be
     * confused, which is exactly what a bare number would have done.
     */
    const [pendingPick, setPendingPick] = useState<string | null>(null);
    /** ⭐ Today's arrivals at this facility, ACROSS EVERY CARRIER. */
    const [checkedIn, setCheckedIn] = useState<CheckInDto[]>([]);
    const [newDriverName, setNewDriverName] = useState('');
    const [newDriverPhone, setNewDriverPhone] = useState('');
    const [newDriverEmail, setNewDriverEmail] = useState('');
    const [driverRoster, setDriverRoster] = useState<
        CarrierDriverAdminRow[] | 'loading' | { error: string }
    >('loading');

    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);
	const [poFinderOpen, setPoFinderOpen] = useState(false);

    // Reset on open
    useEffect(() => {
        if (!open) return;
        setDirection(appt.direction);
        setDockDoorId(appt.dockDoorId);
        setStartLocal(toDatetimeLocalString(parseISO(appt.scheduledStartUtc)));
        setEndLocal(toDatetimeLocalString(parseISO(appt.scheduledEndUtc)));
        setCarrierCode(appt.carrierCode);
        setClientCode(appt.clientCode);
        setDocumentType(appt.documentType || 'PO');
        setPoNumber(appt.poNumber ?? '');
        setContainerNumber(appt.containerNumber ?? '');
        setReferenceNumber(appt.referenceNumber ?? '');
        setPalletCount(appt.palletCount != null ? String(appt.palletCount) : '');
        setComments(appt.comments ?? '');
        // Capabilities: appt.requiredCapabilities is an array of codes; map to ids
        const capIdsFromCodes = appt.requiredCapabilities
            .map((code) => lookups.capabilities.find((c) => c.Code === code)?.CapabilityId)
            .filter((id): id is number => typeof id === 'number');
        setCapabilityIds(capIdsFromCodes);
        setConsignees(
            appt.consignees.length > 0
                ? appt.consignees.map((c) => ({
                      consigneeCode: c.consigneeCode,
                      shipmentNumber: c.shipmentNumber ?? '',
                  }))
                : [{ consigneeCode: '', shipmentNumber: '' }],
        );
        setStagedDrivers(
            appt.drivers.map((d) => ({ appointmentDriverId: d.appointmentDriverId, driverId: d.driverId, name: d.name, phone: d.phone })),
        );
        setDriverMode('existing');
        setPendingPick(null);
        setNewDriverName(''); setNewDriverPhone(''); setNewDriverEmail('');
        setSubmitting(false);
        setSubmitError(null);
    }, [open, appt, lookups.capabilities]);

    // Reload the driver roster whenever the dialog opens or the selected
    // carrier changes — the roster is per-carrier, so switching Carrier
    // means the old list (and any selected driver from it) no longer applies.
    useEffect(() => {
        if (!open || !carrierCode) return;
        let cancelled = false;
        setDriverRoster('loading');
        (async () => {
            try {
                const token = await getToken();
                const drivers = await adminListCarrierDrivers(token, carrierCode);
                if (!cancelled) setDriverRoster(drivers);
            } catch (e) {
                if (!cancelled) setDriverRoster({ error: e instanceof ApiCallError ? e.message : (e instanceof Error ? e.message : 'Failed to load roster.') });
            }
        })();
        return () => { cancelled = true; };
    }, [open, carrierCode, getToken]);

    /**
     * ⭐ TODAY'S ARRIVALS, UNFILTERED BY CARRIER — the whole point of Pete's ask.
     * ⚠️ ALLOWED TO FAIL SILENTLY. The roster half still works, and a failed
     * lookup must not block adding a driver the normal way.
     */
    useEffect(() => {
        if (!open || !appt.facilityCode) return;
        let cancelled = false;
        (async () => {
            try {
                const res = await getCheckIns(await getToken(), appt.facilityCode);
                if (!cancelled) setCheckedIn(res.checkIns ?? []);
            } catch {
                if (!cancelled) setCheckedIn([]);
            }
        })();
        return () => { cancelled = true; };
    }, [open, appt.facilityCode, getToken]);

    /**
     * ⛔ ONLY ARRIVALS WE CAN ACTUALLY ADD. `AddDriverBody` needs a name AND a
     * phone; an arrival still showing "Awaiting driver name" has neither, and
     * listing it would offer a choice that fails on Save.
     * ⚠️ Deduped against what is already staged, by phone — the same man checked
     * in twice is one driver.
     */
    const addableCheckIns = checkedIn.filter((c) => {
        const name = (c.driverName ?? '').trim();
        const phone = (c.driverPhoneEntered ?? '').trim();
        if (!name || !phone) return false;
        return !stagedDrivers.some((d) => (d.phone ?? '').trim() === phone);
    });

    /**
     * ---- ⛔⛔ THE ARRIVALS THAT CANNOT BE PICKED, AND WHY THEY ARE STILL SHOWN
     *
     * > Pete, 2026-09-10: *"Editing an appointment still doesn't show drivers in
     * > Driver Checkin"* — with one truck plainly checked in on the board.
     *
     * ⛔ HE WAS RIGHT AND MY FIRST FIX WAS WRONG IN A FAMILIAR WAY. That arrival
     * reads **"Awaiting driver name"**: its `driverName` is NULL. `AddDriverBody`
     * needs a name AND a phone, so the filter above correctly refused it — and
     * then the empty state said *"nobody has checked in today"*, which is simply
     * **false**. ⚠️ A MESSAGE MORE CONFIDENT THAN THE FILTER BEHIND IT (§4.14):
     * "cannot be added yet" and "does not exist" are different sentences, and I
     * printed the wrong one.
     *
     * ⭐ SO THEY ARE LISTED, DISABLED, WITH THE REASON. The scheduler can see the
     * truck is known to the system and what is missing before it can be used —
     * which is a two-second fix on the arrival card, not a mystery.
     */
    const blockedCheckIns = checkedIn.filter((c) => {
        const name = (c.driverName ?? '').trim();
        const phone = (c.driverPhoneEntered ?? '').trim();
        return !name || !phone;
    });

    // Carrier change invalidates the staged driver list — the roster is
    // per-carrier, so drivers picked from the old carrier's roster no longer
    // apply. Switching back to the appointment's original carrier restores its
    // original drivers; switching to a different carrier clears the list.
    const handleCarrierChange = (code: string) => {
        setCarrierCode(code);
        setStagedDrivers(
            code === appt.carrierCode
                ? appt.drivers.map((d) => ({ appointmentDriverId: d.appointmentDriverId, driverId: d.driverId, name: d.name, phone: d.phone }))
                : [],
        );
        setPendingPick(null);
        setNewDriverName(''); setNewDriverPhone(''); setNewDriverEmail('');
        setDriverMode('existing');
    };

    const toggleCapability = (id: number) => {
        setCapabilityIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
    };

    // Stage a driver add locally (committed on Save, not immediately). Guards
    // against staging the same roster driver twice.
    const stageAddDriver = () => {
        setSubmitError(null);
        if (driverMode === 'existing') {
            if (!pendingPick) { setSubmitError('Select a driver to add, or switch to "Add new driver".'); return; }

            /* ⭐ AN ARRIVAL, NOT A ROSTER ROW. He is staged by NAME AND PHONE
               with a null driverId — exactly like "Add new driver" — because he
               may belong to no carrier roster at all. ⛔ The server creates or
               matches the driver record; inventing an id here would be guessing. */
            if (pendingPick.startsWith('c:')) {
                const c = checkedIn.find((x) => x.checkInId === pendingPick.slice(2));
                if (!c) { setSubmitError('That arrival is no longer on the board.'); return; }
                const name = (c.driverName ?? '').trim();
                const phone = (c.driverPhoneEntered ?? '').trim();
                if (!name || !phone) { setSubmitError('That arrival has no driver name or phone yet.'); return; }
                if (stagedDrivers.some((d) => (d.phone ?? '').trim() === phone)) {
                    setSubmitError('That driver is already on this appointment.');
                    return;
                }
                setStagedDrivers((prev) => [...prev, { appointmentDriverId: null, driverId: null, name, phone }]);
                setPendingPick(null);
                return;
            }

            const rosterId = Number(pendingPick.slice(2));
            if (stagedDrivers.some((d) => d.driverId === rosterId)) {
                setSubmitError('That driver is already on this appointment.');
                return;
            }
            const roster = Array.isArray(driverRoster) ? driverRoster : [];
            const picked = roster.find((d) => d.driverId === rosterId);
            if (!picked) { setSubmitError('Selected driver not found in roster.'); return; }
            setStagedDrivers((prev) => [...prev, { appointmentDriverId: null, driverId: picked.driverId, name: picked.name, phone: picked.phone }]);
            setPendingPick(null);
        } else {
            if (!newDriverName.trim() || !newDriverPhone.trim()) {
                setSubmitError('New driver needs a name and phone.');
                return;
            }
            setStagedDrivers((prev) => [...prev, {
                appointmentDriverId: null,
                driverId: null,
                name: newDriverName.trim(),
                phone: newDriverPhone.trim(),
                email: newDriverEmail.trim() || null,
            }]);
            setNewDriverName(''); setNewDriverPhone(''); setNewDriverEmail('');
        }
    };

    // Stage a removal locally. Existing entries (with appointmentDriverId) and
    // pending adds (without) are both just dropped from the working list; the
    // Save diff figures out which need a DELETE call.
    const stageRemoveDriver = (index: number) => {
        setStagedDrivers((prev) => prev.filter((_, i) => i !== index));
    };

    const handleSave = async () => {
        setSubmitError(null);

        /**
         * ⛔ NO CARRIER CHECK (2026-08-25) — see NewAppointmentDialog. An appointment
         * booked without a carrier must stay editable without one, or a scheduler who
         * created a TBD load could not change its pallet count without inventing a
         * carrier that does not exist yet.
         */
        if (!clientCode) {
            setSubmitError('Client is required.');
            return;
        }
        const isTerminalAppt = appt.statusCode === 'DEPARTED' || appt.statusCode === 'CANCELLED' || appt.statusCode === 'NOSHOW';
        // ⛔ THE CLIENT-SIDE "PO REQUIRED" CHECK IS GONE (#129/#132). Whether a PO
        // is needed is a per-facility, per-scope, per-field policy (migration 151)
        // that only the server can answer — and on THIS screen the old check was the
        // most obstructive of the two, because editing is exactly where a scheduler
        // goes to fill a number in. ⚠️ Do not re-add it: a hardcoded copy in the
        // browser ignores the configuration and refuses bookings at facilities that
        // deliberately allow them.
        const palletNum = Number(palletCount);
        if (palletCount.trim() === '' || !Number.isInteger(palletNum) || palletNum < 0) {
            setSubmitError('Pallet count is required (a whole number, 0 or more).');
            return;
        }

        // Schedule: door + start/end (datetime-local, interpreted as facility-local).
        const newStartUtc = localInputToIsoUtc(startLocal);
        const newEndUtc = localInputToIsoUtc(endLocal);
        if (!newStartUtc || !newEndUtc) {
            setSubmitError('Enter a valid start and end date/time.');
            return;
        }
        if (new Date(newEndUtc).getTime() <= new Date(newStartUtc).getTime()) {
            setSubmitError('End time must be after the start time.');
            return;
        }
        const scheduleChanged =
            dockDoorId !== appt.dockDoorId ||
            new Date(newStartUtc).getTime() !== parseISO(appt.scheduledStartUtc).getTime() ||
            new Date(newEndUtc).getTime() !== parseISO(appt.scheduledEndUtc).getTime();

        let consigneesToSend: EditAppointmentBody['consignees'] = [];
        if (direction === 'OUTBOUND') {
            const cleaned = consignees
                .map((c) => ({
                    consigneeCode: c.consigneeCode.trim(),
                    shipmentNumber: c.shipmentNumber.trim() || null,
                }))
                .filter((c) => c.consigneeCode.length > 0);
            /**
             * ⛔ THE EMPTY-CONSIGNEE REFUSAL WAS HERE AND IS GONE (2026-08-27).
             * Pete: *"Need to remove this requirement."* It blocked edits to loads
             * that had already departed. See the note in schemas.ts.
             *
             * ⚠️ `cleaned` MAY NOW BE EMPTY AND THAT IS ALLOWED — the mapping below
             * handles it, producing an empty list rather than a row with a blank code.
             */
            consigneesToSend = cleaned.map((c, i) => ({
                consigneeCode: c.consigneeCode,
                shipmentNumber: c.shipmentNumber,
                isPrimary: i === 0,
            }));
        }

        setSubmitting(true);
        try {
            const token = await getToken();
            const updated = await editAppointment(token, appt.appointmentId, {
                direction,
                carrierCode,
                clientCode,
                documentType: direction === 'INBOUND' ? documentType : 'SHIPMENT',
                poNumber: direction === 'INBOUND' ? poNumber.trim() || null : null,
                containerNumber: containerNumber.trim() || null,
                referenceNumber: referenceNumber.trim() || null,
                palletCount: palletNum,
                comments: comments.trim() || null,
                requiredCapabilityIds: capabilityIds,
                consignees: consigneesToSend,
            });

            /**
             * ---- ⭐ THE ACTUAL TIMES, IF THEY CHANGED (#138) ----
             *
             * ⛔ SENT ONLY WHEN THE FIELD ACTUALLY MOVED, and each field independently.
             * The API treats an ABSENT key as "leave it alone" and an explicit null as
             * "clear it" — so sending both every time would rewrite a departure nobody
             * touched, and sending neither key when one changed would drop the edit.
             *
             * ⚠️ ITS OWN ENDPOINT, NOT PART OF `editAppointment`. These feed the dwell
             * and labour reports and carry their own audit action (`ActualTimesEdited`)
             * so a corrected time stays distinguishable from an observed one. Folding
             * them into the general edit would lose that distinction on every save.
             */
            const arrivalIso = actualArrivalLocal ? localInputToIsoUtc(actualArrivalLocal) : null;
            const departureIso = actualDepartureLocal ? localInputToIsoUtc(actualDepartureLocal) : null;
            const timePatch: { actualArrivalUtc?: string | null; actualDepartureUtc?: string | null } = {};
            if (arrivalIso !== (appt.actualArrivalUtc ?? null)) timePatch.actualArrivalUtc = arrivalIso;
            if (departureIso !== (appt.actualDepartureUtc ?? null)) timePatch.actualDepartureUtc = departureIso;
            if (Object.keys(timePatch).length > 0) {
                await setActualTimes(token, appt.appointmentId, timePatch);
            }

            // Drivers are committed via their own endpoints (shared with the
            // standalone AssignDriverDialog and the carrier portal) — editAppointment
            // doesn't touch drivers. Diff the staged list against the original:
            // remove originals that are gone, add staged additions. Never for a
            // terminal appointment (UI hides the editor; belt-and-suspenders here).
            if (!isTerminalAppt) {
                const keptIds = new Set(
                    stagedDrivers.filter((d) => d.appointmentDriverId != null).map((d) => d.appointmentDriverId as number),
                );
                // Removals: original entries no longer in the staged list.
                for (const orig of appt.drivers) {
                    if (!keptIds.has(orig.appointmentDriverId)) {
                        await removeAppointmentDriver(token, appt.appointmentId, orig.appointmentDriverId);
                    }
                }
                // Additions: staged entries with no appointmentDriverId yet.
                for (const staged of stagedDrivers) {
                    if (staged.appointmentDriverId == null) {
                        const body = staged.driverId != null
                            ? { driverId: staged.driverId }
                            : { name: staged.name, phone: staged.phone, email: staged.email ?? null };
                        await addAppointmentDriver(token, appt.appointmentId, body);
                    }
                }
            }

            onSaved(
                updated,
                scheduleChanged ? { dockDoorId, newStartUtc, newEndUtc } : undefined,
            );
            onClose();
        } catch (err) {
            if (err instanceof ApiCallError) {
                setSubmitError(err.message);
            } else if (err instanceof Error) {
                setSubmitError(err.message);
            } else {
                setSubmitError('Unknown error');
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>

            {/**
              * ⛔ THE DOCUMENT PREVIEW IS NOT "OUTSIDE", EVEN THOUGH THE DOM SAYS SO.
              *
              * ⚠️ `DocumentPreview` portals itself to document.body so it can sit
              * BESIDE this dialog rather than inside it — that placement is the
              * whole point, you are comparing paperwork against the load. But Radix
              * closes a dialog on any pointer event outside its own subtree, and a
              * portal is outside by definition. So scrolling the PDF, or pressing
              * the preview's own X, read as "the user clicked away" and tore this
              * dialog down underneath it.
              *
              * Pete, 2026-08-25: *"When I scroll on the new dock to see the
              * signature, everything closes... when I 'x' out of the pdf, it doesn't
              * go back to the previous popup, it closes both."* Two symptoms, one
              * cause.
              *
              * ⭐ ESCAPE IS HANDLED THE SAME WAY AND FOR THE SAME REASON: with a
              * preview open, Escape belongs to the preview. Letting it through would
              * close both at once, which is the bug in its keyboard form.
              */}
            <DialogContent
                className="max-w-2xl max-h-[90vh] flex flex-col"
                onPointerDownOutside={(e) => {
                    if ((e.target as HTMLElement | null)?.closest('[data-document-preview]')) e.preventDefault();
                }}
                onInteractOutside={(e) => {
                    if ((e.target as HTMLElement | null)?.closest('[data-document-preview]')) e.preventDefault();
                }}
                /**
                 * ⚠️ ASKED OF THE DOM, NOT OF STATE. The preview's open/closed flag
                 * lives inside PaperworkList, not here — this dialog does not own it
                 * and should not have to. The panel marks itself, so the question
                 * "is a preview open?" can be asked of the document directly.
                 */
                onEscapeKeyDown={(e) => {
                    if (document.querySelector('[data-document-preview]')) e.preventDefault();
                }}
            >
                <DialogHeader>
                    <DialogTitle>Edit {appt.appointmentNumber}</DialogTitle>
                    <DialogDescription>
                        Edit details, date, and time. Changing the date or time reschedules the
                        appointment (limits and door reservations still apply). Status uses the
                        Mark-* buttons.
                    </DialogDescription>
                </DialogHeader>

                {/**
                  * ---- ⛔⛔ NO SECOND SCROLLER, AND NO NEGATIVE MARGIN ---------
                  *
                  * > Pete, 2026-09-10: *"Screen not scrolling - needs right side
                  * > scrollbar."* — and the screenshot showed a HORIZONTAL bar
                  * across the bottom instead.
                  *
                  * ⛔ THIS DIV USED TO CARRY `flex-1 overflow-y-auto min-h-0
                  * -mx-6 px-6`, AND EVERY PART OF THAT WAS WRONG HERE:
                  *
                  *   · `flex-1` / `min-h-0` are **inert**. `DialogContent` wraps
                  *     children in `grid min-h-0 flex-1 gap-4 overflow-y-auto` —
                  *     a GRID, so a flex-child utility does nothing at all.
                  *   · `-mx-6 px-6` made this box **3rem wider than its grid
                  *     track**. The wrapper has `overflow-y-auto`, and CSS
                  *     computes `overflow-x` to `auto` when the other axis is
                  *     not `visible` — so the extra width became the horizontal
                  *     scrollbar, which then ate the vertical space.
                  *   · A second `overflow-y-auto` inside the shell's scroller
                  *     put the content in a nested box that never reached its
                  *     own cap, so the outer one is what actually scrolled.
                  *
                  * ⭐ THE SHELL ALREADY DOES ALL OF THIS CORRECTLY — it keeps
                  * `p-6` on Content OUTSIDE the scroller for exactly this reason,
                  * which is what the negative margin was trying to re-create.
                  * A plain wrapper is the fix.
                  */}
                <div className="space-y-4">

                {submitError && (
                    <div className="bg-destructive/10 border border-destructive/30 rounded-md p-3 flex items-start gap-2">
                        <AlertCircle className="h-4 w-4 text-destructive flex-shrink-0 mt-0.5" />
                        <div className="text-sm text-destructive">{submitError}</div>
                    </div>
                )}

                <div className="grid grid-cols-2 gap-3">
                    {/**
                      * ⭐⭐ THE FOUR TIMES, TOGETHER AND FIRST (Pete, 2026-09-25):
                      * *"Start/End Date need to move to top above Dock Door. Same with
                      * Arrived and Departed."*
                      *
                      * ⛔ THEY WERE SPLIT BY REQUIRED CAPABILITIES. Start and End sat at
                      * field twelve, the actual times at field fifteen, with capability
                      * chips wedged between them - so the two numbers a scheduler reads
                      * AGAINST EACH OTHER were the furthest apart on the form.
                      *   ** FIELDS THAT ARE COMPARED MUST BE ADJACENT. A FORM THAT
                      *      SEPARATES THEM MAKES THE READER HOLD ONE IN THEIR HEAD. **
                      *
                      * ⚠️ SCHEDULED FIRST, ACTUAL SECOND, which is the order they are
                      * read in and the order New Appointment now uses for its own two.
                      */}
                    <div className="col-span-2 sm:col-span-1">
                        <Label htmlFor="e-start">Start</Label>
                        <Input
                            id="e-start"
                            type="datetime-local"
                            value={startLocal}
                            onChange={(e) => setStartLocal(e.target.value)}
                            className="mt-1.5 text-tabular"
                        />
                    </div>
                    <div className="col-span-2 sm:col-span-1">
                        <Label htmlFor="e-end">End</Label>
                        <Input
                            id="e-end"
                            type="datetime-local"
                            value={endLocal}
                            onChange={(e) => setEndLocal(e.target.value)}
                            className="mt-1.5 text-tabular"
                        />
                    </div>
                    {/* ---- ⭐ WHEN IT ACTUALLY HAPPENED (#138) ----

                     Pete, 2026-08-24, importing a day that had already partly run:
                     *"some of the 41 appointments will already have completed so the
                     scheduler may go into the appointment and change the actual
                     Appointment Arrival and departure times."*

                     ---- ⛔⚤ ~~SHOWN ONLY ONCE THERE IS SOMETHING TO CORRECT~~ 2026-09-25

                     The gate was `statusCode === ARRIVED || DEPARTED || a time on file`,
                     and its reasoning was sound: offering these on a REQUESTED
                     appointment "invites somebody to assert an arrival that has not
                     happened". Pete removed it deliberately, having asked in the same
                     breath that the change be attributed:
                       *"Yes on number 1 but log that an arrival or departure time was
                        changed by the person who changed it"*

                     ⭐ THE ATTRIBUTION IS WHAT MAKES IT SAFE, AND IT ALREADY EXISTED.
                     `appointmentRepository.writeAuditDirect` records ActualTimesEdited
                     with the BEFORE and the AFTER and the actor, and logs a status change
                     caused by a time edit separately as StatusChangedByTimeEdit - because
                     "a person asserted this after the fact" and "the system observed it"
                     are different kinds of fact. The History panel renders both.
                       ** A GUARD THAT PREVENTS A BAD ENTRY AND A RECORD THAT ATTRIBUTES
                          ONE SOLVE THE SAME PROBLEM AT DIFFERENT COSTS. THE RECORD COSTS
                          NOTHING TO THE PERSON WITH A LEGITIMATE CORRECTION. **

                     ⚠️ THE BOX STAYS, THE HEADING GOES. Pete: *"keep the box around this
                     one but remove When it actually happend and the text underneath
                     that."* The box is what separates real times from the schedule; the
                     heading was explaining a section that now sits second in the dialog,
                     directly under the scheduled window it contrasts with.

                     ⚠️ THESE ARE NOT THE SCHEDULE. The scheduled window is directly
                     above; this is the record of what the truck really did, and it feeds
                     the dwell and labour reports. */}
                    <div className="col-span-2 rounded-md border bg-muted/30 p-3">
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label htmlFor="e-actual-arrival">Arrived</Label>
                                <Input
                                    id="e-actual-arrival" type="datetime-local"
                                    className="mt-1.5"
                                    value={actualArrivalLocal}
                                    onChange={(e) => setActualArrivalLocal(e.target.value)}
                                />
                            </div>
                            <div>
                                <Label htmlFor="e-actual-departure">Departed</Label>
                                <Input
                                    id="e-actual-departure" type="datetime-local"
                                    className="mt-1.5"
                                    value={actualDepartureLocal}
                                    onChange={(e) => setActualDepartureLocal(e.target.value)}
                                />
                            </div>
                        </div>
                        {/* ⛔ SAID HERE, NOT ONLY IN THE AUDIT. An edited time and a
                            measured one are different kinds of fact, and the person
                            typing it is the one who should know that. */}
                        <p className="text-muted-foreground mt-2 text-xs">
                            A time you type here is recorded as corrected by you, so the
                            reports can still tell it apart from one the system observed.
                        </p>
                    </div>
                    
                    {/* Dock door — matches New. Unassigned + doors valid for the direction. */}
                    <div className="col-span-2 sm:col-span-1">
                        <Label htmlFor="e-door">Dock Door</Label>
                        <Select
                            value={dockDoorId !== null ? String(dockDoorId) : 'UNASSIGNED'}
                            onValueChange={(v) => setDockDoorId(v === 'UNASSIGNED' ? null : Number(v))}
                        >
                            <SelectTrigger id="e-door" className="mt-1.5">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="UNASSIGNED">Unassigned (queue)</SelectItem>
                                {doors
                                    .filter((d) => d.allowedDirection === 'BOTH' || d.allowedDirection === direction)
                                    .map((d) => (
                                        <SelectItem key={d.dockDoorId} value={String(d.dockDoorId)}>
                                            {d.code} <span className="text-muted-foreground ml-1">· {d.name}</span>
                                            {d.allowedDirection !== 'BOTH' && (
                                                <span className="text-muted-foreground ml-1 text-xs">
                                                    ({d.allowedDirection.toLowerCase()})
                                                </span>
                                            )}
                                        </SelectItem>
                                    ))}
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Direction (gated by status) */}
                    <div className="col-span-2 sm:col-span-1">
                        <Label htmlFor="e-direction">Direction</Label>
                        <Select
                            value={direction}
                            onValueChange={(v) => setDirection(v as 'INBOUND' | 'OUTBOUND' | 'BLOCK')}
                            disabled={!directionEditable}
                        >
                            <SelectTrigger id="e-direction" className="mt-1.5">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="INBOUND">Inbound (receiving)</SelectItem>
                                <SelectItem value="OUTBOUND">Outbound (shipping)</SelectItem>
                            </SelectContent>
                        </Select>
                        {!directionEditable && (
                            <p className="text-xs text-muted-foreground mt-1">
                                Direction is locked once the appointment progresses past Approved.
                            </p>
                        )}
                        {directionEditable && direction !== appt.direction && (
                            <p className="text-xs text-amber-700 mt-1">
                                Changing direction may unassign the door if it&apos;s direction-locked.
                                You&apos;ll need to reschedule afterward.
                            </p>
                        )}
                    </div>

                    {/* Document type — only meaningful when inbound */}
                    {direction === 'INBOUND' && (
                        <div className="col-span-2 sm:col-span-1">
                            <Label htmlFor="e-docType">Document Type</Label>
                            <Select value={documentType} onValueChange={setDocumentType}>
                                <SelectTrigger id="e-docType" className="mt-1.5">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="PO">Purchase Order</SelectItem>
                                    <SelectItem value="TO">Transfer Order</SelectItem>
                                    <SelectItem value="OTHER">Other</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    )}

                    {/* Carrier */}
                    <div className="col-span-2 sm:col-span-1">
                        <Label htmlFor="e-carrier">Carrier (optional)</Label>
                        <Select value={carrierCode || NO_SELECTION}
                                onValueChange={(v) => handleCarrierChange(v === NO_SELECTION ? '' : v)}>
                            <SelectTrigger id="e-carrier" className="mt-1.5">
                                <SelectValue placeholder="Select carrier" />
                            </SelectTrigger>
                            <SelectContent>
                                {/**
                                  * ⭐ AN EXPLICIT "NOT KNOWN YET" OPTION (2026-08-25).
                                  * Making the carrier optional in validation is not enough
                                  * if the control has no way to express it — a Select with
                                  * only real carriers in it means a scheduler who does not
                                  * know the carrier must either pick a wrong one or leave a
                                  * field that LOOKS unfinished.
                                  *
                                  * ⛔ IT CARRIES A SENTINEL, NOT THE EMPTY STRING — AND THIS
                                  * PARAGRAPH USED TO SAY THE OPPOSITE. `value=""` made Radix
                                  * throw DURING RENDER ("must have a value prop that is not an
                                  * empty string"), which took the whole dialog down to a white
                                  * screen rather than degrading. The empty string is how a
                                  * Select says "nothing chosen, show the placeholder", so it
                                  * cannot also BE a choice.
                                  *
                                  * ⛔ THE SENTINEL IS TRANSLATED AWAY AT THIS CONTROL, both
                                  * ways, so nothing new reaches the server: the API still
                                  * receives a blank carrier and still stores 'TBD' (arch
                                  * §1R.8). The option is a way to SAY it, not a new carrier.
                                  */}
                                <SelectItem value={NO_SELECTION}>Not known yet (TBD)</SelectItem>
                                {lookups.carriers.map((c) => (
                                    <SelectItem key={c.CarrierCode} value={c.CarrierCode}>
                                        {c.CarrierName}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Client */}
                    <div className="col-span-2 sm:col-span-1">
                        <Label htmlFor="e-client">Client</Label>
                        <Select value={clientCode} onValueChange={setClientCode}>
                            <SelectTrigger id="e-client" className="mt-1.5">
                                <SelectValue placeholder="Select client" />
                            </SelectTrigger>
                            <SelectContent>
                                {lookups.clients.map((c) => (
                                    <SelectItem key={c.ClientCode} value={c.ClientCode}>
                                        {c.ClientName}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    {/**
                      * ⭐⭐ THE LOAD SEARCH SITS UNDER CLIENT (Pete, 2026-09-25):
                      * *"Find PO needs to be under Client, Select a driver under that."*
                      *
                      * ⛔ IT WAS BELOW THE DEPARTURE CHECKLIST, eleven fields down and
                      * behind a panel about what happens when the truck LEAVES - so the
                      * first thing a scheduler wants to do on an edit was the last thing
                      * the form offered.
                      *
                      * ⚠️ AND IT MATCHES New Appointment, which now puts its search in
                      * the same slot for both directions. Two forms over one record that
                      * ask in different orders is what Pete raised this about.
                      */}
                    {/* PO Number (inbound only) */}
                    {direction === 'INBOUND' && (
                        <div className="col-span-2">
                            <Label htmlFor="e-po">PO / Receipt Number</Label>
                           <div className="mt-1.5 flex gap-2">
                                <Input
                                    id="e-po"
                                    value={poNumber}
                                    onChange={(e) => setPoNumber(e.target.value)}
                                    placeholder="PO123456"
                                    className="font-mono"
                                />
                                <Button type="button" variant="default" onClick={() => setPoFinderOpen(true)}>
                                    Find PO
                                </Button>
                            </div>
                         </div>
                    )}

                    {/* Driver — text-only contact, scoped to the carrier selected above.
                        Locked out entirely once the appointment is terminal (matches the
                        server-side rule in appointmentStatus.ts). */}
                    <div className="col-span-2">
                        <Label>Drivers</Label>
                        {(appt.statusCode === 'DEPARTED' || appt.statusCode === 'CANCELLED' || appt.statusCode === 'NOSHOW') ? (
                            <div className="mt-1.5 text-sm text-muted-foreground">
                                {appt.drivers.length > 0
                                    ? `${appt.drivers.map((d) => `${d.name} · ${d.phone}`).join(', ')} (locked — appointment is ${appt.statusCode.toLowerCase()})`
                                    : `No drivers were on file (locked — appointment is ${appt.statusCode.toLowerCase()})`}
                            </div>
                        ) : (
                            <div className="mt-1.5 space-y-2">
                                {/* Staged driver list (committed on Save) */}
                                {stagedDrivers.length > 0 ? (
                                    <div className="border rounded-md divide-y">
                                        {stagedDrivers.map((d, i) => (
                                            <div key={d.appointmentDriverId ?? `pending-${i}`} className="flex items-center justify-between gap-2 px-3 py-1.5">
                                                <div className="min-w-0">
                                                    <span className="text-sm font-medium">{d.name}</span>
                                                    <span className="text-xs text-muted-foreground"> · {d.phone}</span>
                                                    {d.appointmentDriverId == null && <span className="text-[10px] text-blue-600 ml-1">(will add on save)</span>}
                                                </div>
                                                <button type="button" onClick={() => stageRemoveDriver(i)}
                                                    className="text-xs text-destructive hover:underline shrink-0">
                                                    Remove
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-xs text-muted-foreground">No drivers on this appointment.</div>
                                )}

                                {/* Add a driver */}
                                <div className="flex items-center gap-2 text-sm pt-1">
                                    <button type="button" onClick={() => setDriverMode('existing')}
                                        className={`px-3 py-1 rounded-md border text-xs ${driverMode === 'existing' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}>
                                        Pick from roster
                                    </button>
                                    <button type="button" onClick={() => setDriverMode('new')}
                                        className={`px-3 py-1 rounded-md border text-xs ${driverMode === 'new' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}>
                                        Add new driver
                                    </button>
                                </div>

                                {driverMode === 'existing' && (
                                    <div className="flex items-center gap-2">
                                        {driverRoster === 'loading' && <div className="text-xs text-muted-foreground">Loading roster…</div>}
                                        {driverRoster && !Array.isArray(driverRoster) && driverRoster !== 'loading' && (
                                            <div className="text-xs text-destructive">Couldn&apos;t load roster — {driverRoster.error}</div>
                                        )}
                                        {Array.isArray(driverRoster) && (
                                            <>
                                                <Select
                                                    value={pendingPick ?? '__none__'}
                                                    onValueChange={(v) => setPendingPick(v === '__none__' ? null : v)}
                                                >
                                                    <SelectTrigger className="mt-0 flex-1"><SelectValue placeholder="Select a driver…" /></SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="__none__">Select a driver…</SelectItem>

                                                        {/* ⭐ THE CARRIER'S ROSTER, when a carrier is set at all. */}
                                                        {driverRoster.filter(d => d.isActive).map(d => (
                                                            <SelectItem key={`r:${d.driverId}`} value={`r:${d.driverId}`}>
                                                                {d.name} · {d.phone}
                                                            </SelectItem>
                                                        ))}

                                                        {/**
                                                          * ---- ⭐⭐ ANYONE WHO CHECKED IN TODAY ---------------
                                                          *
                                                          * > Pete, 2026-09-10: *"The Select A driver should list
                                                          * > any drivers in the Driver Check-Ins no matter what
                                                          * > carrier they may have been assigned too."*
                                                          *
                                                          * ⛔ THIS IS THE HALF THAT WAS MISSING ENTIRELY. The
                                                          * roster load is gated on `carrierCode`, so an
                                                          * appointment with no carrier — most of them — showed an
                                                          * empty list and no explanation.
                                                          *
                                                          * ⚠️ THE CARRIER IS SHOWN BESIDE EACH NAME, because the
                                                          * whole point is that it may NOT match this
                                                          * appointment's. Hiding it would make two men called
                                                          * Hsieh indistinguishable.
                                                          */}
                                                        {addableCheckIns.length > 0 && (
                                                            <div className="px-2 pb-1 pt-2 text-[10px] font-medium text-muted-foreground">
                                                                Checked in today
                                                            </div>
                                                        )}
                                                        {addableCheckIns.map(c => (
                                                            <SelectItem key={`c:${c.checkInId}`} value={`c:${c.checkInId}`}>
                                                                {c.driverName} · {c.driverPhoneEntered}
                                                                {c.carrierCode ? ` · ${c.carrierCode}` : ' · no carrier'}
                                                            </SelectItem>
                                                        ))}

                                                        {/* ⛔ SHOWN, NOT HIDDEN — see the blockedCheckIns note.
                                                            An arrival the system knows about must never be
                                                            reported as "nobody checked in". */}
                                                        {blockedCheckIns.map(c => (
                                                            <div
                                                                key={`x:${c.checkInId}`}
                                                                className="px-2 py-1 text-[11px] text-muted-foreground"
                                                            >
                                                                {(c.driverName ?? '').trim() || 'Awaiting driver name'}
                                                                {c.driverPhoneEntered ? ` · ${c.driverPhoneEntered}` : ''}
                                                                {' — '}
                                                                {!(c.driverName ?? '').trim()
                                                                    ? 'needs a name on the arrival first'
                                                                    : 'needs a phone on the arrival first'}
                                                            </div>
                                                        ))}

                                                        {/* ⛔ AN EMPTY LIST MUST SAY WHY (§1Z.2). "Nothing here"
                                                            and "we could not look" are different sentences, and a
                                                            silent empty dropdown reads as the second. */}
                                                        {driverRoster.filter(d => d.isActive).length === 0
                                                            && addableCheckIns.length === 0
                                                            && blockedCheckIns.length === 0 && (
                                                            <div className="px-2 py-1.5 text-[11px] text-muted-foreground">
                                                                {carrierCode
                                                                    ? 'No roster drivers for this carrier, and nobody has checked in today.'
                                                                    : 'No carrier is set, so there is no roster — and nobody has checked in today.'}
                                                                {' '}Use “Add new driver”.
                                                            </div>
                                                        )}
                                                    </SelectContent>
                                                </Select>
                                                {/* ⭐ SAME SIZE AS Find PO (Pete, 2026-09-25): *"the button ADD needs to
                                                    match the format that FIND PO has."* ⚠️ The only
                                                    difference was `size="sm"`; both are variant="default".
                                                    Two buttons doing the same KIND of job - commit this
                                                    row - should not be two sizes. */}
                                                <Button type="button" variant="default" onClick={stageAddDriver}>Add</Button>
                                            </>
                                        )}
                                    </div>
                                )}

                                {driverMode === 'new' && (
                                    <div className="space-y-2">
                                        <Input value={newDriverName} onChange={(e) => setNewDriverName(e.target.value)} placeholder="Driver name" />
                                        <Input value={newDriverPhone} onChange={(e) => setNewDriverPhone(e.target.value)} placeholder="+1 555 555 5555" />
                                        <Input value={newDriverEmail} onChange={(e) => setNewDriverEmail(e.target.value)} placeholder="Email (optional)" />
                                        <Button type="button" variant="default" size="sm" onClick={stageAddDriver}>Add driver</Button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/**
                      * ---- ⭐ THE LOAD'S PAPERWORK, WHERE THE EDITING HAPPENS -------
                      *
                      * Pete, 2026-08-25: *"maybe on the Edit Screen, you list the
                      * paperwork like you do in the right column with the option to
                      * preview and delete?"*
                      *
                      * ⭐ EDITING A LOAD AND CHECKING ITS PAPERWORK ARE ONE ERRAND. A
                      * scheduler fixing a PO or a container number is usually reading
                      * the BOL while they do it; sending them out to a second dialog
                      * turns one job into three.
                      *
                      * ⛔ THE SAME DOSSIER THE PAPERWORK DIALOG READS — not a second
                      * source of the same facts. It draws less on purpose and says so.
                      */}
                    <div className="col-span-2">
                        {/**
                          * ⭐ PER-ROW SIGN AND PRINT (#245a). No judgement layer here on
                          * purpose: the documents are on screen, so the scheduler points
                          * at one and there is nothing to infer.
                          *
                          * ⚠️ PRINT OPENS THE PDF; IT CANNOT CHOOSE THE READER. Adobe or
                          * the browser's own viewer is the operating system's file
                          * association, not the app's decision.
                          */}
                        <PaperworkList
                            appointmentId={String(appt.appointmentId)}
                            getToken={getToken}
                            /* ⭐ #303 — passing a facility is what turns this list from
                               read-only into "drop a file here". Pete: *"Need to be able
                               to add paperwork here too."* */
                            facilityCode={appt.facilityCode}
                            key={signedTick}
                            onSign={(d) => setSignRow(d)}
                            onPrint={(d) => { void openForPrint(d); }}
                        />
                        {/* ⛔ THE PRINT OUTCOME, AND IT IS NEUTRAL, NOT RED. "opened in a
                            new tab" and "this type needs Acrobat" are both correct answers,
                            not faults — and the dialog often opens BEHIND this window, so
                            without a line here a working button looks like a dead one. */}
                        {printMsg && (
                            <div className="mt-1 flex items-start justify-between gap-2 rounded border
                                            bg-muted/40 px-2 py-1 text-[11px]">
                                <span>{printMsg}</span>
                                <button type="button" onClick={() => setPrintMsg(null)}
                                        className="shrink-0 underline">dismiss</button>
                            </div>
                        )}

                        {/**
                          * ⭐ #317 — DIRECTLY UNDER THE PAPERWORK, AND THAT PLACEMENT IS
                          * THE POINT. Pete: *"Almost like a checklist before they depart
                          * kind of thing."* The list above says what IS on the load; this
                          * says what the load is not finished without. Reading them apart
                          * is how "we have four documents" gets mistaken for "we have the
                          * right four".
                          *
                          * ⚠️ IT DERIVES FROM THE SAME DOCUMENTS, so a file added through
                          * the drop zone above changes this panel on its next read — which
                          * is why the panel refetches rather than being handed a snapshot.
                          */}
                        <div className="mt-4 border-t pt-3">
                            <DepartureChecklistPanel
                                /* ⭐ Pete, 2026-09-25: the section should not sit on this
                                   form when there is nothing to show. The panel keeps its
                                   loud empty state everywhere else — this dialog is not
                                   the departure gate, `outboundRelease.ts` is. */
                                hideWhenEmpty
                                appointmentId={String(appt.appointmentId)}
                                /* ⛔ #317/5 — WITHOUT THIS THE HANDOUT BUTTONS ARE NOT
                                   OFFERED. The facility decides the document rules the
                                   filed copy lands under, so it is required rather than
                                   guessed. */
                                facilityCode={appt.facilityCode}
                                getToken={getToken}
                                key={signedTick}
                            />
                        </div>
                    </div>


                    {/* Container number (#130). Shown for both directions. */}
                    <div className="col-span-2">
                        <Label htmlFor="e-container">Container Number</Label>
                        <Input
                            id="e-container"
                            className="mt-1.5 font-mono"
                            value={containerNumber}
                            onChange={(e) => setContainerNumber(e.target.value)}
                            placeholder="optional"
                        />
                    </div>
                    {/* DIRECTION IS PASSED HERE, and it matters more than it looks.
                        Until 2026-08-14 this call site omitted it, so editing an
                        OUTBOUND appointment opened a picker that (a) described
                        itself as searching purchase orders, and (b) searched every
                        order type -- which means Camelot receipt 12605 AND shipment
                        12605 both came back, two different trucks under one number.
                        That collision is the entire reason the search is
                        direction-aware. NewAppointmentDialog was fixed last week;
                        this screen renders the same component and was missed. */}
                    <PoFinder
                        open={poFinderOpen}
                        onClose={() => setPoFinderOpen(false)}
                        getToken={getToken}
                        facilityCode={appt.facilityCode}
                        anchorDate={startLocal.slice(0, 10)}
                        direction={direction === 'BLOCK' ? undefined : direction}
                        initialQuery={direction === 'OUTBOUND' ? referenceNumber : poNumber}
                        onSelect={({ poNumber: picked, clientCode: pickedClient, referenceNumber: pickedRef }) => {
                            if (direction === 'OUTBOUND') {
                                // An outbound appointment has no PO -- save() sends
                                // poNumber: null for anything not INBOUND, so writing
                                // the picked number there would discard it on save.
                                // The document number IS what a scheduler tracks an
                                // outbound by, so it belongs in Reference Number.
                                setReferenceNumber(picked);
                                if (pickedClient) setClientCode(pickedClient);
                            } else {
                                setPoNumber(picked);
                                if (pickedClient) setClientCode(pickedClient);
                                if (pickedRef) setReferenceNumber(pickedRef);
                            }
                        }}
                    />
                    {/* Consignees (outbound only) */}
                    {direction === 'OUTBOUND' && (
                        <div className="col-span-2">
                            <Label>Consignee(s)</Label>
                            <div className="mt-1.5 space-y-2">
                                {consignees.map((c, idx) => (
                                    <div key={idx} className="flex gap-2 items-start">
                                        <Input
                                            value={c.consigneeCode}
                                            onChange={(e) => {
                                                const next = [...consignees];
                                                next[idx] = {
                                                    ...next[idx],
                                                    consigneeCode: e.target.value.toUpperCase(),
                                                };
                                                setConsignees(next);
                                            }}
                                            placeholder={
                                                idx === 0
                                                    ? 'Primary consignee code'
                                                    : 'Additional consignee code'
                                            }
                                            className="font-mono flex-1"
                                        />
                                        <Input
                                            value={c.shipmentNumber}
                                            onChange={(e) => {
                                                const next = [...consignees];
                                                next[idx] = {
                                                    ...next[idx],
                                                    shipmentNumber: e.target.value,
                                                };
                                                setConsignees(next);
                                            }}
                                            placeholder="Shipment # (optional)"
                                            className="font-mono flex-1"
                                        />
                                        {consignees.length > 1 && (
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="icon"
                                                onClick={() =>
                                                    setConsignees(consignees.filter((_, i) => i !== idx))
                                                }
                                                aria-label="Remove consignee"
                                            >
                                                ×
                                            </Button>
                                        )}
                                    </div>
                                ))}
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() =>
                                        setConsignees([
                                            ...consignees,
                                            { consigneeCode: '', shipmentNumber: '' },
                                        ])
                                    }
                                >
                                    + Add another consignee
                                </Button>
                            </div>
                        </div>
                    )}

                    {/* Reference + Pallet Count */}
                    <div className="col-span-2 sm:col-span-1">
                        <Label htmlFor="e-ref">Reference Number (optional)</Label>
                        {/* OUTBOUND'S ONLY WAY INTO THE PICKER. The Find PO button
                            lives inside a {direction === 'INBOUND'} block, so before
                            this an outbound appointment had NO route to the search at
                            all -- poFinderOpen could never be set. Same gap Pete
                            caught on New Appointment ("Still not seeing a PO Search
                            field on Outbound"), one screen over. variant="default" so
                            it is visibly the same button as Find PO. */}
                        <div className="mt-1.5 flex gap-2">
                            <Input
                                id="e-ref"
                                value={referenceNumber}
                                onChange={(e) => setReferenceNumber(e.target.value)}
                                placeholder={direction === 'OUTBOUND'
                                    ? 'Shipment #, reference, trailer #'
                                    : 'BOL, trailer #, internal reference'}
                            />
                            {direction === 'OUTBOUND' && (
                                <Button
                                    type="button"
                                    variant="default"
                                    onClick={() => setPoFinderOpen(true)}
                                >
                                    Find shipment
                                </Button>
                            )}
                        </div>
                    </div>
                    <div className="col-span-2 sm:col-span-1">
                        <Label htmlFor="e-pallets">Pallet Count <span className="text-destructive">*</span></Label>
                        <Input
                            id="e-pallets"
                            type="number"
                            min={0}
                            step={1}
                            inputMode="numeric"
                            value={palletCount}
                            onChange={(e) => setPalletCount(e.target.value)}
                            placeholder="e.g. 12"
                            className="mt-1.5 text-tabular"
                        />
                    </div>

                    {/* Start / End — matches New. Changing these reschedules. */}

                    {/* Capabilities */}
                    {lookups.capabilities.length > 0 && (
                        <div className="col-span-2">
                            <Label>Required Capabilities</Label>
                            <div className="mt-1.5 flex flex-wrap gap-2">
                                {lookups.capabilities.map((c) => {
                                    const selected = capabilityIds.includes(c.CapabilityId);
                                    return (
                                        <button
                                            key={c.CapabilityId}
                                            type="button"
                                            onClick={() => toggleCapability(c.CapabilityId)}
                                            className={`px-2 py-1 rounded text-xs border transition ${
                                                selected
                                                    ? 'bg-primary text-primary-foreground border-primary'
                                                    : 'bg-background hover:bg-muted border-input'
                                            }`}
                                        >
                                            {c.Name}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}


                    {/* Comments */}
                    <div className="col-span-2">
                        <Label htmlFor="e-comments">Comments</Label>
                        <Textarea
                            id="e-comments"
                            value={comments}
                            onChange={(e) => setComments(e.target.value)}
                            placeholder="Notes for the dock supervisor or carrier"
                            className="mt-1.5"
                            rows={3}
                        />
                    </div>
                </div>
                </div>

                <DialogFooter className="shrink-0">
                    <Button variant="ghost" onClick={onClose} disabled={submitting}>
                        Cancel
                    </Button>
                    <Button onClick={handleSave} disabled={submitting}>
                        {submitting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                        Save changes
                    </Button>
                </DialogFooter>
            </DialogContent>

            <SignDocumentDialog
                open={signRow !== null}
                document={signRow}
                facilityCode={appt.facilityCode}
                carrierCode={appt.carrierCode}
                appointmentNumber={appt.appointmentNumber}
                getToken={getToken}
                onClose={() => setSignRow(null)}
                onSigned={() => { setSignRow(null); setSignedTick(t => t + 1); }}
            />
        </Dialog>
    );
}

/** Format a Date as YYYY-MM-DDTHH:mm, the format <input type=datetime-local> wants. */
function toDatetimeLocalString(d: Date): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Parse the YYYY-MM-DDTHH:mm produced by <input type=datetime-local> as a local-time Date. */
function parseDatetimeLocal(s: string): Date | null {
    if (!s) return null;
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? null : d;
}

/** Convert local-time datetime-local input value to ISO UTC string for the server. */
function localInputToIsoUtc(localStr: string): string | null {
    const d = parseDatetimeLocal(localStr);
    return d ? d.toISOString() : null;
}
